import React, { useContext, useEffect, useRef, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useMutation, useQuery } from '@apollo/client/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { orderBy } from '@progress/kendo-data-query';
import { GridDataType } from '@progress/kendo-react-grid';
import { getData } from 'country-list';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import ErrorMessage from '@components-clean/core/error';
import useGridCustomCell, { INamedItem } from '@components-clean/core/grid/custom-cell';
import ToggleDeletedRecordsIcon from '@components-clean/core/grid/deleted-records-icon';
import { GridMenuItemProps } from '@components-clean/core/grid/grid-menu/grid-menu-item';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import {
    ExtendedCompleteRiskFields,
    ExtendedRiskObservationFields,
} from '@pages-clean/risk-assessment/risk-log/constants';
import AddActionDialog, { RiskActionFormInputs } from '@pages-clean/risk-assessment/risk-log/dialogs/add-action-dialog';
import AddObservationDialog, {
    RiskObservationFormInputs,
} from '@pages-clean/risk-assessment/risk-log/dialogs/add-observation-dialog';
import DeleteObservationDialog from '@pages-clean/risk-assessment/risk-log/dialogs/delete-observation-dialog';
import RestoreObservationDialog from '@pages-clean/risk-assessment/risk-log/dialogs/restore-observation-dialog';
import { mapActionToFlattenedAction } from '@pages-clean/risk-assessment/risk-log/helper';
import { RiskMonitoringContext } from '@pages-clean/risk-assessment/risk-monitoring/interfaces/context';
import { assumeUtc } from '@utils-clean/date-time-helpers';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import {
    AuditApplicationArea,
    AuditDataType,
    AuditServiceType,
    CountryInputType,
    NamedItemType,
    RiskActionType,
} from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate, stringDateWithoutTimeFromLocal } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { GET_NAMED_ITEMS_BY_TYPE, GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY } from '@queries/common';
import { getNamedItemsByType, getNamedItemsByTypeForStudy } from '@queries/generated/common.generated';
import {
    riskObservations_OpraQueries_riskObservations_RiskObservation,
    riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction,
} from '@queries/generated/legacy-risk-assessment.generated';
import {
    saveRiskObservation,
    saveRiskObservationVariables,
    saveRiskAction,
    saveRiskActionVariables,
} from '@queries/generated/risk-assessment.generated';
import {
    GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_RISKS,
    SAVE_RISK_ACTION,
    SAVE_RISK_OBSERVATION,
} from '@queries/risk-assessment';

const ManageObservationsGrid = () => {
    const user = useAuthorization();
    const canView = user?.canRead?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.Read,
    });
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    if (!canView) return null;
    const { current, setCurrent } = useContext(RiskMonitoringContext);
    const { study, showDeleted, selectedObservation } = current;
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const { t: tCommon } = useTranslation('common');
    const { t: tWarning } = useTranslation('ram', { keyPrefix: 'ctqf.ctqFactors.grid.filterSortWarning' });
    const exportFileName = t('grids.observations.exportFileName');
    const { TooltipCell, ActiveInactiveCell } = useGridCustomCell();
    const [flattenedObservations, setFlattenedObservations] = useState([]);
    const showAddObservationDialog = useBoolean(false);
    const showAddActionDialog = useBoolean(false);
    const showDeleteObservationDialog = useBoolean(false);
    const showRestoreObservationDialog = useBoolean(false);
    const addObservationAction = useRef(false);
    const updateObservationAction = useRef(false);
    const deletedObservationId = useRef(null);
    const restoredObservationId = useRef(null);
    const showFilterAppliedDialog = useBoolean(false);
    const {
        value: clearSelectedRows,
        setTrue: setClearSelectedRowsTrue,
        setFalse: setClearSelectedRowsFalse,
    } = useBoolean(false);

    const [saveObservationMutation] = useMutation<saveRiskObservation, saveRiskObservationVariables>(
        SAVE_RISK_OBSERVATION,
    );
    const [saveActionMutation] = useMutation<saveRiskAction, saveRiskActionVariables>(SAVE_RISK_ACTION);

    const { error, data, refetch } = useQuery(GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_RISKS, {
        variables: { studyId: study?.id },
    });

    const { data: riskVendorData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.VENDOR },
    });

    const { data: riskObservationClassificationData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.RISK_OBSERVATION_CLASSIFICATION },
    });

    const countries = getData().map((countryObject: { code: string; name: string }) => ({
        code: countryObject.code,
        name: countryObject.name,
    }));

    const { data: responsibleFunctionsData } = useQuery<getNamedItemsByTypeForStudy>(
        GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY,
        {
            variables: {
                studyId: study?.id,
                type: NamedItemType.RESPONSIBLE_FUNCTION,
            },
        },
    );

    useEffect(() => {
        if (
            riskVendorData &&
            responsibleFunctionsData &&
            riskObservationClassificationData &&
            data &&
            data.risks &&
            data.riskObservations
        ) {
            const riskIds = data.risks.map((r: { id: string }) => r.id);
            const array = (
                showDeleted
                    ? data.riskObservations
                    : data.riskObservations.filter(
                          (ob: riskObservations_OpraQueries_riskObservations_RiskObservation) => !ob.deleted,
                      )
            )
                .filter((ob: riskObservations_OpraQueries_riskObservations_RiskObservation) =>
                    riskIds.includes(ob.riskId),
                )
                .map((item: riskObservations_OpraQueries_riskObservations_RiskObservation) => {
                    return {
                        ...item,
                        riskRecordId: data.risks.find((risk: { id: string }) => risk.id === item.riskId).recordId,
                        createdBy: item.createdBy ? `${item.createdBy.firstName} ${item.createdBy.lastName}` : '',
                        updatedBy: item.updatedBy ? `${item.updatedBy.firstName} ${item.updatedBy.lastName}` : '',
                        convertedOwner: item.owner
                            ? {
                                  name: `${item.owner.firstName} ${item.owner.lastName}`,
                                  active: item.owner.active,
                              }
                            : {
                                  name: '',
                                  active: true,
                              },
                        recentActionDate:
                            item.actions &&
                            item.actions
                                .filter((activeActions) => activeActions && !activeActions.deleted)
                                .map((action) => action && action.requestDate)
                                .sort()
                                .reverse()[0],
                        numActions:
                            (item.actions &&
                                item.actions.length &&
                                item.actions.filter((action) => action && !action.deleted).length) ??
                            0,
                        numOpenActions:
                            (item.actions &&
                                item.actions.length &&
                                item.actions.filter((action) => action && !action.deleted && !action.completedDate)
                                    .length) ??
                            0,
                        ...(item.deleted && item.audit
                            ? {
                                  reasonForDeletion: item.audit.reason,
                                  deletedDate: item.audit.created,
                                  deletedBy: `${item.audit.userFirstName} ${item.audit.userLastName}`,
                              }
                            : {}),
                        countryList: item.countries?.map((a) => a?.name).join(', '),
                        vendorList: item.vendors?.map((a) => a?.name).join(', '),
                        actions: item.actions?.map((action) => ({ ...action, observationId: item.id })),
                    };
                });

            if (deletedObservationId.current) {
                const updatedObs = array.find(
                    (obs: ExtendedRiskObservationFields) => obs.id === deletedObservationId.current,
                );
                setCurrent({ ...current, selectedObservation: updatedObs });
            } else if (restoredObservationId.current) {
                const updatedObs = array.find(
                    (obs: ExtendedRiskObservationFields) => obs.id === restoredObservationId.current,
                );
                setCurrent({ ...current, selectedObservation: updatedObs });
            } else if (selectedObservation) {
                const updatedObs = array.find(
                    (obs: ExtendedRiskObservationFields) => obs.id === selectedObservation.id,
                );
                setCurrent({ ...current, selectedObservation: updatedObs });
            }
            setFlattenedObservations(orderBy(array, [{ field: 'updated', dir: 'desc' }]));
        }
    }, [data, riskVendorData, responsibleFunctionsData, riskObservationClassificationData, showDeleted]);

    useEffect(() => {
        if (clearSelectedRows) {
            setClearSelectedRowsFalse();
        }
    }, [clearSelectedRows]);

    const onAddObservationClose = () => {
        showAddObservationDialog.setFalse();
    };

    const onSubmitSaveObservation = async (addObservationData: RiskObservationFormInputs) => {
        saveObservationMutation({
            variables: {
                observation: {
                    id: addObservationAction.current ? undefined : selectedObservation?.id,
                    studyId: study?.id,
                    riskId: current.selectedObservationRisk?.id,
                    description: addObservationData.description,
                    classificationId: addObservationData.classification?.id,
                    vendorIds: addObservationData.vendors?.map((v) => v.id) ?? [],
                    countries: addObservationData.countries?.map((c) => c as CountryInputType) ?? [],
                    responsibleFunctionId: addObservationData.responsibleFunction?.id,
                    ownerId: addObservationData.owner?.id,
                    potentialCause: addObservationData.potentialCause,
                    confirmedCause: addObservationData.confirmedCause,
                    observationDate: addObservationData?.observationDate
                        ? stringDateWithoutTimeFromLocal(addObservationData.observationDate)
                        : null,
                    closedDate: addObservationData?.closedDate
                        ? stringDateWithoutTimeFromLocal(addObservationData.closedDate)
                        : null,
                    resolution: addObservationData.resolution,
                    comments: addObservationData.comments,
                },
            },
        })
            .then(async (response) => {
                if (!response?.data || !response.data?.saveRiskObservation) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                updateObservationAction.current = !addObservationAction.current;
                showAddObservationDialog.setFalse();
                await refetch();
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const onDeleteObservationClose = () => {
        showDeleteObservationDialog.setFalse();
    };

    const onSubmitDeleteObservation = async () => {
        deletedObservationId.current = selectedObservation?.id;
        await refetch();
        ShowSuccess(tCommon('actionsSuccessfulToast'));
        showDeleteObservationDialog.setFalse();
    };

    const onRestoreObservationClose = () => {
        showRestoreObservationDialog.setFalse();
    };

    const onSubmitRestoreObservation = async () => {
        restoredObservationId.current = selectedObservation?.id;
        await refetch();
        ShowSuccess(tCommon('actionsSuccessfulToast'));
        showRestoreObservationDialog.setFalse();
    };

    const onAddActionClose = () => {
        showAddActionDialog.setFalse();
    };

    const onSubmitAddAction = async (addActionData: RiskActionFormInputs) => {
        saveActionMutation({
            variables: {
                action: {
                    type: RiskActionType[addActionData.type?.id as keyof typeof RiskActionType],
                    ownerId: addActionData.owner?.id,
                    responsibleFunctionId: addActionData.responsibleFunction?.id,
                    requestDate: assumeUtc(addActionData.requestedDate),
                    description: addActionData.description,
                    result: addActionData.result,
                    comments: addActionData.comments,
                    expectedCompleteDate: addActionData.expectedCompletionDate
                        ? assumeUtc(addActionData.expectedCompletionDate)
                        : undefined,
                    completedDate: addActionData.completedDate ? assumeUtc(addActionData.completedDate) : undefined,
                },
                riskId: selectedObservation?.riskId,
                observationId: selectedObservation?.id,
            },
        })
            .then(async (response) => {
                if (!response?.data || !response.data?.saveRiskAction) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                showAddActionDialog.setFalse();

                const riskRecordId = data.risks.find(
                    (risk: { id: string }) => risk.id === selectedObservation?.riskId,
                ).recordId;

                setCurrent({
                    ...current,
                    selectedAction:
                        mapActionToFlattenedAction(
                            response.data
                                ?.saveRiskAction as riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction,
                            selectedObservation as unknown as riskObservations_OpraQueries_riskObservations_RiskObservation,
                            t(`shared.${response.data?.saveRiskAction?.type?.toLowerCase()}`) as string,
                            riskRecordId,
                        ) ?? undefined,
                });
                await refetch();
                setCurrent((prev) => ({
                    ...prev,
                    actionsGridFiltersResetToken: (prev.actionsGridFiltersResetToken ?? 0) + 1,
                }));
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const columns: GridColumnProps[] = [
        ...(showDeleted
            ? [
                  {
                      id: 'deleted',
                      field: 'deleted',
                      title: t('grids.observations.columns.isDeleted'),
                      lockedByDefault: true,
                      cells: { data: YesNoCell },
                      minWidth: 100,
                      filter: 'boolean' as GridDataType,
                  },
              ]
            : []),
        {
            id: 'status',
            field: 'status',
            title: t('grids.observations.columns.status'),
            lockedByDefault: true,
            minWidth: 120,
            lovData: [t('shared.open'), t('shared.closed')],
        },
        {
            id: 'actions',
            field: 'numActions',
            title: t('grids.observations.columns.actions'),
            minWidth: 80,
            lockedByDefault: true,
            filter: 'numeric',
        },
        {
            id: 'riskId',
            field: 'riskRecordId',
            title: t('grids.observations.columns.riskId'),
            lockedByDefault: true,
            minWidth: 100,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
            alwaysExportColumn: true,
            showByDefault: false,
        },
        {
            id: 'statement',
            field: 'riskStatement',
            title: t('grids.observations.columns.statement'),
            lockedByDefault: true,
            minWidth: 250,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
            alwaysExportColumn: true,
        },
        {
            id: 'description',
            field: 'description',
            title: t('grids.observations.columns.observation'),
            lockedByDefault: true,
            minWidth: 250,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'observationDate',
            field: 'observationDate',
            title: t('grids.observations.columns.observationDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 130,
            cellName: 'DateCell',
        },
        {
            id: 'vendors',
            field: 'vendorList',
            title: t('grids.observations.columns.vendors'),
            minWidth: 200,
            filter: 'text',
            lovData: riskVendorData?.namedItemsByType
                ? riskVendorData.namedItemsByType.map((item) => item?.name as string)
                : [],
            lovFilterContains: true,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                    lovArrayField: 'vendors',
                }),
            },
        },
        {
            id: 'countries',
            field: 'countryList',
            title: t('grids.observations.columns.countries'),
            minWidth: 200,
            filter: 'text',
            lovData: countries.map((c) => c.name),
            lovFilterContains: true,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                    countryArrayField: 'countries',
                }),
            },
        },
        {
            id: 'classification',
            field: 'classification',
            title: t('grids.observations.columns.classification'),
            minWidth: 120,
            filter: 'text',
            filterKey: 'name',
            lovData: riskObservationClassificationData?.namedItemsByType
                ? riskObservationClassificationData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'potentialCause',
            field: 'potentialCause',
            title: t('grids.observations.columns.potentialCause'),
            width: 1,
            minWidth: 200,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'confirmedCause',
            field: 'confirmedCause',
            title: t('grids.observations.columns.confirmedCause'),
            width: 1,
            minWidth: 200,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'owner',
            field: 'convertedOwner',
            title: t('grids.observations.columns.owner'),
            width: 1,
            minWidth: 150,
            filterKey: 'name',
            filter: 'text',
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'responsibleFunction',
            field: 'responsibleFunction',
            title: t('grids.observations.columns.responsibleFunction'),
            width: 180,
            filter: 'text',
            filterKey: 'name',
            lovData: responsibleFunctionsData?.namedItemsByTypeForStudy
                ? responsibleFunctionsData.namedItemsByTypeForStudy
                      .map((item) => item?.name as string)
                      .sort((a, b) => {
                          return a.localeCompare(b);
                      })
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'comments',
            field: 'comments',
            title: t('grids.observations.columns.comments'),
            width: 250,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'closedDate',
            field: 'closedDate',
            title: t('grids.observations.columns.closedDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            id: 'resolution',
            field: 'resolution',
            title: t('grids.observations.columns.resolution'),
            width: 1,
            minWidth: 150,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'recentActionDate',
            field: 'recentActionDate',
            title: t('grids.observations.columns.recentActionDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            id: 'openActions',
            field: 'numOpenActions',
            title: t('grids.observations.columns.openActions'),
            minWidth: 100,
            filter: 'numeric',
        },
        {
            id: 'updatedBy',
            field: 'updatedBy',
            title: t('grids.observations.columns.updatedBy'),
            minWidth: 150,
        },
        {
            id: 'updated',
            field: 'updated',
            title: t('grids.observations.columns.updated'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 130,
            cellName: 'DateCell',
        },
        ...(showDeleted
            ? [
                  {
                      id: 'reasonForDeletion',
                      field: 'reasonForDeletion',
                      title: t('grids.observations.columns.reasonForDeletion'),
                      width: 250,
                      filter: 'text' as GridDataType,
                      cells: {
                          data: TooltipCell({
                              isCsv: false,
                          }),
                      },
                  },
                  {
                      id: 'deletedBy',
                      field: 'deletedBy',
                      title: t('grids.observations.columns.deletedBy'),
                      minWidth: 150,
                  },
                  {
                      id: 'deletedDate',
                      field: 'deletedDate',
                      title: t('grids.observations.columns.deleted'),
                      filter: 'date' as GridDataType,
                      cells: { data: DateTimeCell('DD-MMM-yyyy') },
                      minWidth: 130,
                      cellName: 'DateCell',
                  },
              ]
            : []),
        {
            id: 'createdBy',
            field: 'createdBy',
            title: t('grids.observations.columns.createdBy'),
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'created',
            field: 'created',
            title: t('grids.observations.columns.created'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 130,
            cellName: 'DateCell',
            showByDefault: false,
        },
        {
            width: 1,
            minWidth: 120,
            id: 'recordId',
            field: 'recordId',
            showByDefault: false,
            title: 'ID',
        },
    ];

    if (error) return <ErrorMessage />;

    if (!data) return <Loading />;

    const customGridMenuItems: GridMenuItemProps[] = [
        {
            title: showDeleted ? tCommon('hideDeletedRecords') : tCommon('displayDeletedRecords'),
            'data-testid': 'riskMonitoring-grid-show-deleted-records',
            customIcon: <ToggleDeletedRecordsIcon showDeleted={showDeleted} />,
            onClick: () => {
                setCurrent({ ...current, showDeleted: !current.showDeleted });
            },
            ignoreReadOnlyContext: true,
        },
    ];

    const setRiskAndObservation = (dataitem: unknown) => {
        const obs = dataitem as ExtendedRiskObservationFields;
        const risk = data.risks.find((r: { id: string }) => r.id === obs?.riskId);
        setCurrent({ ...current, selectedObservation: obs, selectedObservationRisk: risk });
    };

    return (
        <>
            <Grid
                excludeHiddenRowMenuActions
                title={`${t('grids.observations.title')} ${!showDeleted ? '' : ` - ${tCommon('all')}`}`}
                customGridMenuItems={customGridMenuItems}
                maxHeight="100%"
                testDataId="riskMonitoring-manageObservations-grid"
                data={flattenedObservations}
                columns={columns}
                filterable
                useLoadingOverlay
                showAddIcon={false}
                canEdit={canEdit}
                scrollable="virtual"
                dataItemKey="id"
                defaultSelectedItem={
                    addObservationAction.current ||
                    restoredObservationId.current ||
                    deletedObservationId.current ||
                    updateObservationAction.current
                        ? selectedObservation
                        : undefined
                }
                selectable={{
                    enabled: true,
                    drag: false,
                    cell: false,
                    mode: 'single',
                }}
                filterListener={() => {
                    setClearSelectedRowsTrue();

                    setCurrent({
                        ...current,
                        selectedObservation: undefined,
                        selectedObservationRisk: undefined,
                    });
                }}
                clearGridSelectedRows={clearSelectedRows}
                onSelectionChange={({ startDataItem }) => {
                    const riskObservation = startDataItem as ExtendedRiskObservationFields;
                    if (riskObservation && selectedObservation?.id !== riskObservation?.id) {
                        addObservationAction.current = false;
                        updateObservationAction.current = false;
                        deletedObservationId.current = null;
                        restoredObservationId.current = null;
                        const risk = data.risks.find((r: { id: string }) => r.id === riskObservation?.riskId);
                        setCurrent({
                            ...current,
                            selectedObservation: riskObservation,
                            selectedObservationRisk: risk,
                            actionsGridFiltersResetToken: current.actionsGridIsFiltered
                                ? (current.actionsGridFiltersResetToken ?? 0) + 1
                                : current.actionsGridFiltersResetToken,
                        });
                    }
                }}
                exportFilename={`${exportFileName}_${study?.studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate(
                    'DD-MMM-YYYY',
                )}`}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                removeCardBorder
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('grids.observations.actions.edit'),
                        onClick: (dataItem) => {
                            addObservationAction.current = false;
                            setRiskAndObservation(dataItem);
                            showAddObservationDialog.setTrue();
                        },
                        disabled: !canEdit,
                        showMenuItem: (dataItem) => {
                            const obs = dataItem as ExtendedRiskObservationFields;
                            return !obs.deleted;
                        },
                        icon: ['far', 'pen-to-square'],
                    },
                    {
                        id: 2,
                        label: t('grids.observations.actions.add'),
                        onClick: (dataItem: unknown) => {
                            const obs = dataItem as ExtendedRiskObservationFields;
                            if (
                                current.actionsGridIsFiltered &&
                                selectedObservation &&
                                obs.id === selectedObservation.id
                            ) {
                                showFilterAppliedDialog.setTrue();
                            } else {
                                setRiskAndObservation(dataItem);
                                showAddActionDialog.setTrue();
                            }
                        },
                        icon: ['far', 'add'] as IconProp,
                        rowDisabled: (dataItem) =>
                            !canEdit || (dataItem as ExtendedRiskObservationFields).closedDate !== null,
                        showMenuItem: (dataItem) => {
                            const obs = dataItem as ExtendedRiskObservationFields;
                            return !obs.deleted;
                        },
                    },
                    {
                        id: 3,
                        label: t('grids.observations.actions.delete'),
                        onClick: (dataItem) => {
                            addObservationAction.current = false;
                            setRiskAndObservation(dataItem);
                            showDeleteObservationDialog.setTrue();
                        },
                        showMenuItem: (dataItem) => {
                            const obs = dataItem as ExtendedRiskObservationFields;
                            return !obs.deleted;
                        },
                        icon: ['fas', 'trash-xmark'],
                        colorProp: 'Red',
                        disabled: !canEdit,
                    },
                    {
                        id: 4,
                        label: t('grids.observations.actions.restore'),
                        onClick: (dataItem) => {
                            addObservationAction.current = false;
                            setRiskAndObservation(dataItem);
                            showRestoreObservationDialog.setTrue();
                        },
                        showMenuItem: (dataItem) => {
                            const obs = dataItem as ExtendedRiskObservationFields;
                            return obs.deleted ?? false;
                        },
                        icon: ['far', 'arrow-rotate-backward'],
                        disabled: !canEdit,
                    },
                ]}
                persistentStorageKey={String(PersistentStorageKeys['risk-monitoring-manage-obs'])}
            />
            {showFilterAppliedDialog.value && (
                <ConfirmationDialog
                    dataTestId="rm-confirm-add-action"
                    title={tWarning('title')}
                    onSubmit={() => {
                        showFilterAppliedDialog.setFalse();
                        showAddActionDialog.setTrue();
                    }}
                    onClose={() => {
                        showAddActionDialog.setFalse();
                        showFilterAppliedDialog.setFalse();
                    }}
                    primaryBtnLabel={tCommon('continue')}
                    secondaryBtnLabel={tCommon('close')}
                    width={500}
                >
                    {tWarning('body')}
                </ConfirmationDialog>
            )}
            {showAddObservationDialog.value && current.selectedObservationRisk && (
                <AddObservationDialog
                    onClose={onAddObservationClose}
                    onSubmit={onSubmitSaveObservation}
                    studyRef={study?.id}
                    observation={addObservationAction.current ? undefined : selectedObservation}
                    risk={current.selectedObservationRisk as unknown as ExtendedCompleteRiskFields}
                />
            )}
            {showAddActionDialog.value && selectedObservation && (
                <AddActionDialog
                    onClose={onAddActionClose}
                    onSubmit={onSubmitAddAction}
                    observation={selectedObservation}
                    studyRef={study?.id}
                />
            )}
            {showDeleteObservationDialog.value && selectedObservation && current.selectedObservationRisk && (
                <DeleteObservationDialog
                    onClose={onDeleteObservationClose}
                    onSubmit={onSubmitDeleteObservation}
                    observation={selectedObservation}
                    studyId={study?.id}
                    riskId={current.selectedObservationRisk?.id}
                />
            )}
            {showRestoreObservationDialog.value && selectedObservation && current.selectedObservationRisk && (
                <RestoreObservationDialog
                    onClose={onRestoreObservationClose}
                    onSubmit={onSubmitRestoreObservation}
                    observation={selectedObservation}
                    studyId={study?.id}
                    riskId={current.selectedObservationRisk?.id}
                />
            )}
        </>
    );
};

export default ManageObservationsGrid;
